import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  Info,
  MessageCircle,
  Phone,
  Minus,
  Plus,
  Sparkles,
  PersonStanding,
  Monitor,
  Dumbbell,
} from "lucide-react";

const PAGE_PATH = "/double-chin-removal-mumbai";
const PAGE_URL = `https://drvickyjain.com${PAGE_PATH}`;
const COSMETIC_PATH = "/cosmetic-surgery-in-mumbai";
const NECK_LIFT_PATH = "/neck-lift-surgery-mumbai";
const CHIN_PATH = "/chin-augmentation-in-mumbai";
const LIPO_PATH = "/liposuction-surgery-mumbai";

const DOUBLE_CHIN_FAQS = [
  {
    question: "Can exercise remove a double chin?",
    answer:
      "Exercise reduces overall body fat, which may reduce submental fat proportionally in some patients. Specific neck exercises tone the anterior neck muscles but do not selectively reduce submental fat. For patients with genetically determined submental fat deposits that are disproportionate to the rest of their body, exercise will not adequately address the double chin. Liposuction or fat dissolving injections are the only treatments that directly reduce submental fat.",
  },
  {
    question: "What is the most effective treatment for double chin?",
    answer:
      "This depends entirely on the cause. For fat-dominant double chin in a patient with good skin elasticity, submental liposuction is the most effective treatment, producing immediate and permanent fat reduction. For skin laxity, a neck lift is the most effective structural correction. For mild cases where the patient prefers non-surgical treatment, fat dissolving injections for fat or radiofrequency for skin laxity are the most appropriate options. There is no single most effective treatment independent of the cause.",
  },
  {
    question: "Is double chin liposuction permanent?",
    answer:
      "The fat cells removed by liposuction are permanently eliminated and do not regenerate. The result is long-lasting provided weight remains stable. Significant weight gain after liposuction deposits new fat in the remaining cells throughout the body, which can partially restore submental fullness. Patients who maintain a stable weight preserve the liposuction result permanently.",
  },
  {
    question: "How many fat dissolving injection sessions are needed?",
    answer:
      "Most patients require 2 to 4 sessions of deoxycholic acid injections spaced 4 to 8 weeks apart. The degree of fat reduction per session is modest and the results accumulate across sessions. The total number depends on the initial volume of submental fat and the target reduction. Sessions produce noticeable swelling and numbness for several weeks after each treatment that resolves before the next session.",
  },
  {
    question: "Is double chin liposuction painful?",
    answer:
      "The procedure is performed under local anesthesia. The anesthesia injection produces brief discomfort. The liposuction itself is painless. Post-procedure swelling and mild soreness for 2 to 3 days are managed with standard pain medication. Most patients describe the experience as significantly less uncomfortable than anticipated.",
  },
  {
    question: "Will liposuction leave a scar?",
    answer:
      "The access incision for submental liposuction is 2 to 3 millimeters and is placed in the natural submental crease beneath the chin where it is not visible at normal viewing distance. The scar fades to near-invisible within 3 to 6 months.",
  },
  {
    question: "Can a non-surgical treatment replace neck lift surgery?",
    answer:
      "For mild skin laxity, non-surgical treatments including radiofrequency and HIFU produce meaningful improvement without surgery. For moderate to significant skin excess, platysmal banding, and structural laxity, non-surgical treatments produce insufficient correction to be a meaningful substitute for neck lift surgery. The improvement they produce is valuable in the right patient, but applying them to a patient who needs surgery produces disappointment. Dr. Jain advises directly when a patient's anatomy requires surgical correction.",
  },
  {
    question: "Can double chin treatment be combined with chin augmentation?",
    answer:
      "Yes, and this combination is common. When the chin lacks forward projection, the jaw-to-neck angle is blunted even in the absence of significant submental fat. Chin filler or a chin implant combined with submental liposuction addresses both the fat beneath the chin and the forward projection of the chin itself, improving the profile from two angles simultaneously.",
  },
];

const possibleTreatmentNames = [
  "Submental liposuction",
  "Fat dissolving injections (deoxycholic acid)",
  "Neck lift surgery",
  "Radiofrequency skin tightening",
  "HIFU (high-intensity focused ultrasound)",
  "Chin augmentation",
];

const possibleTreatmentSchema = possibleTreatmentNames.map((name) => ({
  "@type": "MedicalTherapy",
  name,
}));

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Submental Fullness (Double Chin)",
      description:
        "Excess volume beneath the chin from submental fat, skin laxity, platysmal changes, or combined causes — managed with cause-appropriate surgical and non-surgical options.",
      possibleTreatment: possibleTreatmentSchema,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Submental liposuction",
      description:
        "Tumescent liposuction of the submental and jawline region through a small submental incision for permanent reduction of diet-resistant submental fat when skin elasticity is adequate or combined with tightening.",
      procedureType: "Surgical",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Submental region" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: DOUBLE_CHIN_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function DoubleChinJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: COSMETIC_PATH },
    { name: "Double Chin Removal", href: PAGE_PATH },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic · Face &amp; Neck</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">Double Chin Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Submental Fat Reduction &amp; Jawline Contouring by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Double Chin Removal" icon="✨" size="hero"  imageSrc={HERO.doubleChinRemoval} imageAlt="Double chin treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is a Double Chin?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A double chin, clinically called submental fullness, is excess volume beneath the chin that creates a second fold in the jaw-to-neck transition. This fullness can be caused by submental fat, loose skin with reduced elasticity, laxity of the platysma muscle, or a combination of all three. The appropriate treatment depends on which of these components is dominant in each individual patient.
            </p>
            <p>
              Submental fat as a cause of double chin is often genetic and can occur in patients who are lean elsewhere. The submental area is a site of preferential fat deposition in some individuals, meaning it does not reduce proportionally when the rest of the body loses fat. Diet and exercise can reduce overall body fat but cannot target the submental region specifically. Skin laxity as a cause is more common with aging, where collagen reduction and the effect of gravity over time cause the skin beneath the jaw to become loose and drooping.
            </p>
            <p>
              Dr. Vicky Jain treats double chin using submental liposuction for fat-dominant cases,{" "}
              <Link to={NECK_LIFT_PATH} className="text-gold hover:underline font-medium">
                neck lift
              </Link>{" "}
              for significant skin laxity, non-surgical tightening for early laxity, and combination approaches when both fat and skin are contributing. The treatment plan is based on a clinical assessment of which components are causing the fullness before any treatment is recommended.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TreatmentPrincipleAlert() {
  return (
    <section className="bg-warm-white pb-10 md:pb-14" data-testid="treatment-principle-alert" aria-labelledby="dc-alert-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-xl py-6 px-4 md:px-6 ml-auto">
          <Info className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle id="dc-alert-heading" className="font-serif text-lg text-premium-purple pl-7 mb-2">
            Treatment must match the cause
          </AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 leading-relaxed">
            The right approach depends on whether{" "}
            <strong className="font-medium text-deep-navy">fat</strong>,{" "}
            <strong className="font-medium text-deep-navy">skin laxity</strong>, or{" "}
            <strong className="font-medium text-deep-navy">both</strong> is driving your submental fullness. Treating fat alone when lax skin is the main problem—or tightening skin alone when fat is the main volume—leads to incomplete or disappointing results. Assessment before treatment is essential.
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Double Chin Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most common mistake in double chin treatment is applying a single modality without properly identifying the cause. Liposuction in a patient whose primary problem is skin laxity removes fat but leaves the loose skin less supported, potentially worsening the sagging appearance. Non-surgical skin tightening in a patient with significant submental fat produces inadequate results because the underlying volume causing the fullness has not been addressed. Dr. Jain assesses the relative contribution of fat, skin, and muscle in each patient before recommending treatment.
              </p>
              <p>
                For patients with a combination of fat and early skin laxity, submental liposuction with concurrent use of a skin-tightening modality often produces the best result. Younger patients with good skin elasticity tolerate liposuction alone well because the skin contracts after fat removal. Older patients with reduced elasticity may need skin tightening alongside or after liposuction to produce a clean jaw-to-neck contour. Patients with significant skin excess and platysmal banding require a{" "}
                <Link to={NECK_LIFT_PATH} className="text-white/80 hover:underline font-medium">
                  neck lift
                </Link>{" "}
                for the structural correction that neither liposuction nor non-surgical tightening achieves.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const CAUSE_CARDS = [
  {
    title: "Submental Fat",
    copy: "The most common cause. Fat deposits beneath the chin and along the jaw-to-neck transition. In some patients this is genetically determined and present even at a lean body weight. In others it is proportional to overall body fat. Either way, it does not respond to targeted exercise and only reduces proportionally with significant overall fat loss.",
  },
  {
    title: "Skin Laxity",
    copy: "Loose skin beneath the jaw caused by collagen reduction with age, significant weight loss, or sun damage. The skin cannot maintain its tension against the pull of gravity and droops below the jawline. This is a structural problem that liposuction alone does not correct.",
  },
  {
    title: "Platysmal Banding",
    copy: "The platysma muscle runs vertically from the jaw to the collarbone. When it loses tone with age or weight loss, it creates visible vertical bands in the neck that contribute to the fullness and aged appearance beneath the jaw. Botulinum toxin can relax early platysmal bands. More significant banding requires surgical platysmaplasty.",
  },
  {
    title: "Genetics",
    copy: "Some individuals have a genetic predisposition to submental fat deposition regardless of overall body weight. A family history of double chin in lean relatives is a strong indicator of genetic submental fat that will persist regardless of diet and exercise.",
  },
  {
    title: "Weight Gain",
    copy: "Generalized weight gain increases submental fat alongside fat elsewhere in the body. In these patients weight loss reduces the double chin proportionally, though genetic submental fat deposits may persist even after significant weight reduction.",
  },
  {
    title: "Poor Posture",
    copy: "Chronic forward head posture affects the muscle tone of the anterior neck and can contribute to the appearance of fullness beneath the jaw. This is the only contributing cause that is meaningfully addressable with exercises and postural correction.",
  },
];

function CausesSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="causes-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Causes a Double Chin?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {CAUSE_CARDS.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const NON_SURGICAL_OPTIONS = [
  {
    title: "Fat Dissolving Injections (Deoxycholic Acid)",
    copy: "Deoxycholic acid injected into the submental fat breaks down fat cell membranes, causing gradual fat reduction over multiple sessions. Approved specifically for submental fat. Two to four sessions spaced 4 to 8 weeks apart are typically needed. Results are gradual and less dramatic than liposuction. Produces mild swelling and numbness for several weeks after each session. Appropriate for mild to moderate submental fat in patients who want to avoid surgery. Not appropriate when skin laxity is the dominant concern.",
  },
  {
    title: "Radiofrequency Skin Tightening",
    copy: "RF energy delivered to the submental skin and tissue heats the collagen in a controlled manner, stimulating new collagen production and contracting existing collagen. Improves skin firmness and early laxity over multiple sessions. Used as a standalone treatment for early skin laxity without significant fat, or as an adjunct to liposuction to support skin contraction after fat removal.",
  },
  {
    title: "HIFU",
    copy: "High-intensity focused ultrasound delivers energy to the deeper tissue layers beneath the skin, creating a tightening effect without surface damage. Used for early to moderate skin laxity in patients who prefer no downtime. Multiple sessions are typically required. Results are gradual and more modest than surgical neck lift.",
  },
  {
    title: "Thread Lift",
    copy: "Dissolving threads inserted beneath the chin and along the jawline provide immediate mechanical lifting of sagging tissue. Results last 12 to 18 months. Appropriate for mild tissue descent without significant fat or skin excess.",
  },
];

const SURGICAL_OPTIONS = [
  {
    title: "Submental Liposuction",
    copy: "The most effective permanent treatment for fat-dominant double chin. Fat is removed through a small incision of 2 to 3 millimeters beneath the chin using a fine cannula. The procedure takes 30 to 60 minutes under local anesthesia. No hospital admission is required. Fat cells removed are permanently eliminated. Younger patients with good skin elasticity achieve excellent results from liposuction alone. Patients with reduced elasticity may benefit from concurrent skin tightening.",
  },
  {
    title: "Neck Lift Surgery",
    copy: (
      <>
        The definitive treatment for significant skin laxity, platysmal banding, and excess skin beneath the jaw and along the neck. Incisions are placed behind the ears and in the submental crease. The platysma is tightened, excess skin is removed, and the jaw-to-neck contour is restored surgically. Produces the most comprehensive and longest-lasting correction of all double chin treatments. Appropriate when skin excess and muscle laxity are the primary components. More detail:{" "}
        <Link to={NECK_LIFT_PATH} className="text-gold hover:underline font-medium">
          neck lift
        </Link>
        .
      </>
    ),
  },
  {
    title: "Chin Augmentation",
    copy: (
      <>
        Occasionally combined with submental liposuction when the chin lacks forward projection, contributing to the visual double chin appearance. A small chin implant or chin filler adds projection to the chin, which improves the jaw-to-neck angle and makes the submental fat appear less prominent in profile. See{" "}
        <Link to={CHIN_PATH} className="text-gold hover:underline font-medium">
          chin augmentation
        </Link>
        .
      </>
    ),
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Double Chin Removal Treatment Options</h2>

        <h3 className="font-serif text-2xl text-white mb-2 text-center md:text-left max-w-6xl mx-auto">Non-Surgical Options</h3>
        <div className="h-px bg-white/20 max-w-6xl mx-auto mb-8" aria-hidden />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-14">
          {NON_SURGICAL_OPTIONS.map((opt) => (
            <div key={opt.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h4 className="font-serif text-lg text-premium-purple mb-4">{opt.title}</h4>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{opt.copy}</p>
            </div>
          ))}
        </div>

        <h3 className="font-serif text-2xl text-white mb-2 text-center md:text-left max-w-6xl mx-auto">Surgical Options</h3>
        <div className="h-px bg-white/20 max-w-6xl mx-auto mb-8" aria-hidden />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {SURGICAL_OPTIONS.map((opt) => (
            <div key={opt.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h4 className="font-serif text-lg text-premium-purple mb-4">{opt.title}</h4>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{opt.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const SELECTION_ROWS = [
  {
    scenario: "Fat only, good skin elasticity, younger patient",
    rec: "Submental liposuction or fat dissolving injections. Liposuction for more immediate and complete result. Injections for those preferring non-surgical approach.",
  },
  {
    scenario: "Fat with early skin laxity",
    rec: "Submental liposuction combined with radiofrequency or non-surgical tightening. Skin contractility after liposuction is supported by the tightening treatment.",
  },
  {
    scenario: "Skin laxity without significant fat",
    rec: "Radiofrequency, HIFU, or thread lift for mild cases. Neck lift for moderate to significant skin excess.",
  },
  {
    scenario: "Platysmal banding, significant skin excess",
    rec: "Neck lift surgery. Non-surgical treatments are insufficient for this level of structural laxity.",
  },
  {
    scenario: "Mixed fat and skin, significant concern",
    rec: "Combination of liposuction and neck lift or neck lift with liposuction component.",
  },
];

function TreatmentSelectionSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-selection-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Which Double Chin Treatment Is Right for You?</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[32%]">
                  Your situation
                </th>
                <th scope="col" className="py-4 pl-2 font-serif text-premium-purple">
                  Typical direction
                </th>
              </tr>
            </thead>
            <tbody>
              {SELECTION_ROWS.map((row) => (
                <tr key={row.scenario} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.scenario}
                  </th>
                  <td className="py-4 pl-2 text-deep-navy/70 align-top">{row.rec}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/60 text-sm text-center">
          For significant skin excess or banding, a{" "}
          <Link to={NECK_LIFT_PATH} className="text-gold hover:underline font-medium">
            neck lift consultation
          </Link>{" "}
          clarifies whether surgery is indicated.
        </p>
      </div>
    </section>
  );
}

function LiposuctionHowSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="liposuction-how-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Double Chin Liposuction is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Submental liposuction takes 30 to 60 minutes under local anesthesia. No hospital admission is required. Dr. Jain marks the treatment zone and the planned approach before administering local anesthetic to the submental area. A tumescent solution is infiltrated into the fat, which reduces bleeding and makes the fat easier to remove.
            </p>
            <p>
              A small incision of 2 to 3 millimeters is made in the natural submental crease beneath the chin. A fine cannula is passed through this incision and moved in controlled passes through the fat to remove it. The cannula is also directed along the jawline bilaterally to contour the jaw angle and the transition from jaw to neck. The symmetry of fat removal between the two sides is assessed during the procedure.
            </p>
            <p>
              A compression chin strap is applied immediately after the procedure. This is worn continuously for the first week and during the day for the following 1 to 2 weeks. The compression strap reduces swelling, maintains the tissue against the new contour during healing, and prevents fluid accumulation in the treated space.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild to moderate swelling and bruising in the submental area. The chin strap stays on continuously. Minor discomfort managed with standard pain relief. Normal daily activity is not significantly restricted.",
      icon: Sparkles,
    },
    {
      period: "Week 1",
      description:
        "Desk work and light daily activity resume fully. Swelling begins to reduce. The improved jaw contour is visible even through residual swelling.",
      icon: PersonStanding,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Visible improvement in the jaw-to-neck transition. Chin strap worn during the day. Exercise and strenuous activity resume progressively.",
      icon: Dumbbell,
    },
    {
      period: "Months 1 to 3",
      description:
        "Final contour is visible as all swelling resolves. The jaw-to-neck transition reads as sharper and more defined. Results are permanent for the removed fat.",
      icon: Monitor,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CanReturnSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="can-return-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Long-Term Results</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Can a Double Chin Return After Treatment?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The fat cells removed by liposuction or dissolved by fat dissolving injections are permanently eliminated. They do not regenerate. For patients who maintain a stable weight after treatment, the result is long-lasting and the double chin does not return. However, significant weight gain after treatment deposits new fat in the remaining fat cells throughout the body, including any remaining cells in the submental area. This can reduce the visual improvement if weight gain is substantial.
            </p>
            <p>
              For skin laxity treated non-surgically, results last as long as the treatment maintains collagen stimulation, typically requiring maintenance sessions. Aging continues and gravity continues to affect skin elasticity, so patients who choose non-surgical tightening for skin laxity need ongoing maintenance treatments to sustain the result. Surgical neck lift results are more durable because the structural cause is addressed directly, but aging continues after surgery as it does everywhere on the face and body.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Double Chin Treatment?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult bothered by the appearance of a double chin who has a specific identifiable cause, whether fat, skin laxity, or both, is appropriate for consultation. There is no minimum severity. A patient with a modest double chin that significantly affects their profile in photos and in social settings has a valid reason for treatment. Patients who have tried weight loss and found that the submental area did not respond proportionally are appropriate candidates for submental liposuction regardless of their overall body weight.
            </p>
            <p>
              The appropriate treatment depends on age, skin quality, the dominant cause, and the patient&apos;s preference for surgical versus non-surgical approaches. Younger patients with good skin elasticity get excellent results from liposuction alone. Patients in their 40s and beyond with skin laxity as a component need a different approach. Patients with unrealistic expectations about non-surgical treatments achieving the same result as surgery should understand the limitations of each approach before deciding. Dr. Jain advises which treatment is actually appropriate for each patient&apos;s anatomy rather than defaulting to the patient&apos;s preference for a specific modality.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {DOUBLE_CHIN_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`double-chin-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Double Chin Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the contribution of fat, skin laxity, and muscle to your double chin to determine which treatment or combination of treatments addresses your specific anatomy. You leave with a clear recommendation based on what your situation requires, not a default to the most or least invasive option.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Neck Lift",
      description: "Structural correction for significant skin laxity and platysmal banding beyond what liposuction addresses.",
      href: NECK_LIFT_PATH,
    },
    {
      name: "Chin Augmentation",
      description: "Improve jaw projection alongside submental fat reduction for complete profile enhancement.",
      href: CHIN_PATH,
    },
    {
      name: "Liposuction",
      description: "Body fat removal from multiple areas alongside chin contouring in a single procedure.",
      href: LIPO_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function DoubleChinRemovalPage() {
  return (
    <>
      <SEO
        title="Double Chin Removal | Dr. Vicky Jain"
        description="Submental fat, loose chin skin, or platysmal banding creating a double chin. Dr. Vicky Jain provides surgical and non-surgical double chin treatment. Book today."
        canonical={PAGE_PATH}
      />
      <DoubleChinJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <TreatmentPrincipleAlert />
      <DrJainApproachSection />
      <CausesSection />
      <TreatmentOptionsSection />
      <TreatmentSelectionSection />
      <LiposuctionHowSection />
      <RecoveryTimelineSection />
      <CanReturnSection />
      <GoodCandidateSection />
      <PatientTestimonials procedureName="Facelift" />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
